import { useState, useCallback } from "react";

const DEFAULT_FALLBACK = "/logo.png";

/**
 * Drop-in <img> replacement with three superpowers:
 *
 *  1. **Auto-fallback**: if the real source 404s or hits an error, we swap
 *     in a local fallback (defaults to /logo.png) so the user never sees
 *     a broken-image icon again.
 *
 *  2. **Auto-optimize** (opt-in via `optimize` prop): rewrites a local
 *     `/api/static/...` or whitelisted remote URL to `/api/img?url=...&w=...`
 *     so the backend serves a resized WebP with a 1-year cache header.
 *     This shaves ~70% off page weight on heavy product grids.
 *
 *  3. **Native lazy-loading + async decode** by default — keeps Lighthouse
 *     LCP scores high without any extra work.
 *
 * Use this for any product / gallery / blog hero image. Plain <img> is fine
 * for icons or transparent svgs that don't need resizing.
 */
export default function SafeImage({
  src,
  alt = "",
  fallback = DEFAULT_FALLBACK,
  optimize = false,
  width,           // hint passed to /api/img&w=
  quality = 80,    // /api/img&q=
  className = "",
  loading = "lazy",
  decoding = "async",
  onError,
  ...rest
}) {
  const initialSrc = optimize && src ? optimizedUrl(src, width, quality) : src;
  const [currentSrc, setCurrentSrc] = useState(initialSrc);
  const [failed, setFailed] = useState(false);

  const handleError = useCallback(
    (e) => {
      if (!failed && currentSrc !== fallback) {
        setFailed(true);
        setCurrentSrc(fallback);
      }
      if (onError) onError(e);
    },
    [failed, currentSrc, fallback, onError],
  );

  return (
    <img
      src={currentSrc || fallback}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      onError={handleError}
      data-fallback={failed ? "true" : undefined}
      {...rest}
    />
  );
}

/**
 * Rewrite a source URL to go through the /api/img optimizer when it'll
 * actually save us bytes. Local /api/static/* JPEGs are already small
 * and served straight off the disk by FastAPI — running them through PIL
 * just to shave 50KB off a 100KB file isn't worth the CPU storm when a
 * listing page renders 100+ images at once. We therefore SKIP local
 * static paths and only optimize:
 *   - Off-site Unsplash / canonical CDN images (where the network win is huge)
 *   - Any URL where the caller explicitly requested a width <= 200 (true
 *     thumbnails — worth the resize because the source is much larger).
 *
 * Returns the original URL unchanged if the source isn't a candidate
 * (data URLs, already-optimized routes, etc).
 */
export function optimizedUrl(src, width, quality = 80) {
  if (!src || typeof src !== "string") return src;
  if (src.startsWith("data:")) return src;        // base64 already inline
  if (src.startsWith("/api/img")) return src;     // already optimized
  if (src.startsWith("/logo.png")) return src;    // tiny static asset
  const isStatic = src.startsWith("/api/static/");
  const isImported = src.startsWith("/api/static/imported/");
  const isUnsplash = src.includes("images.unsplash.com");
  const isPexels = src.includes("images.pexels.com");
  const isCanonical = src.startsWith("https://torontocustomtshirts.ca/");
  // Supplier CDNs (SanMar / S&S / ListPerfectly) 403 for browsers or ship
  // huge JPEGs — ALWAYS route them through /api/img to load + optimize.
  const isSupplier = src.includes("media.sanmarcanada.com") || src.includes("cdn.ssactivewear.com") || src.includes("media.listperfectly.com");
  if (!isStatic && !isUnsplash && !isPexels && !isCanonical && !isSupplier) return src;
  // Local static images are already disk-cached; only optimize genuine tiny
  // thumbnails — EXCEPT imported ready-made mockups, which are large raw
  // JPEGs and must always be resized to WebP.
  const wantsTinyThumb = width && width > 0 && width <= 200;
  if (isStatic && !isImported && !wantsTinyThumb) return src;
  const w = width && width > 0 ? `&w=${Math.min(2400, Math.max(16, Math.round(width)))}` : "";
  const q = `&q=${Math.min(95, Math.max(30, quality))}`;
  return `/api/img?url=${encodeURIComponent(src)}${w}${q}`;
}
